<template>
    <div class="typeahead-field">
        <!-- The pick replaces the search box so stray typing can't undo it. -->
        <div
            v-if="showPicked"
            class="typeahead-field__picked"
            :data-test-id="`${dataTestIdPrefix}-selected`"
        >
            <suggestion-row
                :suggestion="modelValue"
                :data-test-id-prefix="`${dataTestIdPrefix}-selected`"
            />
            <button
                v-if="!disabled"
                type="button"
                class="typeahead-field__remove"
                :aria-label="`Remove ${modelValue.name}`"
                :data-test-id="`${dataTestIdPrefix}-remove-button`"
                @click="removeSelection"
            >
                &times;
            </button>
        </div>

        <div
            v-else
            class="typeahead-field__search"
            @keydown.capture="onFieldKeydown"
            @invalid.capture="onInvalid"
        >
            <auto-complete
                ref="autocomplete"
                v-model="draft"
                :input-id="inputId"
                :suggestions="suggestions"
                :disabled="disabled"
                :loading="loading"
                :delay="debounceMs"
                :min-length="minLength"
                :placeholder="placeholder"
                :empty-search-message="activeEmptySearchMessage"
                option-label="name"
                option-disabled="unselectable"
                data-key="id"
                :force-selection="!clearOnSelect"
                :dt="optionTokens"
                :pt="{
                    pcInputText: {
                        root: {
                            'data-test-id': `${dataTestIdPrefix}-input`,
                            'aria-invalid': showValidationMessage ? 'true' : undefined,
                            'aria-describedby': showValidationMessage ? validationMessageId : undefined,
                        },
                    },
                    option: ({ context }) => ({
                        'data-test-id': `${dataTestIdPrefix}-option-${context.option.name}`,
                    }),
                }"
                @complete="onComplete"
                @item-select="onItemSelect"
                @input="onInput"
                @focus="onFocus"
            >
                <template
                    v-if="$slots['overlay-header']"
                    #header
                >
                    <div
                        ref="overlayHeader"
                        class="typeahead-field__overlay-header"
                        @keydown="onHeaderKeydown"
                    >
                        <slot name="overlay-header"></slot>
                    </div>
                </template>

                <template #option="{ option }">
                    <suggestion-row
                        :suggestion="option"
                        :status="option.status"
                        :data-test-id-prefix="`${dataTestIdPrefix}-option`"
                    />
                </template>

                <!-- Shown while the list is empty, including while the first page loads. -->
                <template #empty>
                    <span
                        v-if="loading"
                        class="typeahead-field__loading"
                        :data-test-id="`${dataTestIdPrefix}-loading`"
                    >
                        <i
                            class="pi pi-spin pi-spinner"
                            aria-hidden="true"
                        ></i>
                        Searching…
                    </span>
                    <template v-else>{{ activeEmptySearchMessage }}</template>
                </template>

                <template #footer>
                    <div
                        v-if="total > 0 && !loading"
                        class="typeahead-field__footer"
                    >
                        <span :data-test-id="`${dataTestIdPrefix}-result-count`">
                            Showing
                            <span :data-test-id="`${dataTestIdPrefix}-result-count-shown`">{{ shownCount }}</span>
                            of
                            <span :data-test-id="`${dataTestIdPrefix}-result-count-total`">{{ totalLabel }}</span>
                            {{ total === 1 ? 'match' : 'matches' }}
                        </span>
                        <!--
                            mousedown.prevent keeps focus in the input so the list stays open.
                            Arrow keys reach this too (onMoreKeydown).
                        -->
                        <button
                            v-if="hasMore"
                            ref="moreButton"
                            type="button"
                            class="typeahead-field__more"
                            :disabled="loadingMore"
                            :data-test-id="`${dataTestIdPrefix}-load-more-button`"
                            @mousedown.prevent
                            @click="loadMore"
                            @keydown="onMoreKeydown"
                        >
                            <template v-if="loadingMore">
                                <i
                                    class="pi pi-spin pi-spinner"
                                    aria-hidden="true"
                                ></i>
                                Loading…
                            </template>
                            <template v-else>
                                Load next
                                <span :data-test-id="`${dataTestIdPrefix}-load-more-count`">{{ nextPageSize }}</span>
                            </template>
                        </button>
                    </div>
                </template>
            </auto-complete>

            <i
                v-if="hasTypedText && !disabled"
                class="pi pi-times typeahead-field__clear"
                role="button"
                tabindex="0"
                aria-label="Clear the search text"
                :data-test-id="`${dataTestIdPrefix}-clear-button`"
                @click="clearText"
                @keydown.enter.prevent="clearText"
                @keydown.space.prevent="clearText"
            ></i>
        </div>

        <!--
            Replaces the native bubble, which would open the list and cover it.
            role="alert" because focus doesn't move here.
        -->
        <p
            v-if="showValidationMessage && validationMessage"
            :id="validationMessageId"
            class="typeahead-field__error"
            role="alert"
            :data-test-id="`${dataTestIdPrefix}-validation-message`"
        >
            {{ validationMessage }}
        </p>

        <p
            v-if="loadError"
            class="typeahead-field__error"
            :data-test-id="`${dataTestIdPrefix}-error`"
        >
            {{ loadError }}
        </p>
    </div>
</template>

<script>
import AutoComplete from 'primevue/autocomplete'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import { isSelectable } from '@/constants/suggestion-statuses'
import SEARCH_DEBOUNCE_MS from '@/constants/search-debounce'
import SuggestionRow from './SuggestionRow.vue'

const TYPEAHEAD_FIELD = 'typeahead'
const MIN_QUERY_LENGTH = 1
const PAGE_SIZE = 25

// App green for the highlighted option.
const OPTION_TOKENS = {
    option: {
        focusBackground: '#41b883',
        selectedFocusBackground: '#41b883',
        focusColor: '#ffffff',
        selectedFocusColor: '#ffffff',
    },
}

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]'

/**
 * Tabbable elements under `root`, in document order.
 *
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
function focusables(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter((element) => element.tabIndex >= 0)
}

export default {
    name: 'TypeaheadField',

    components: {
        AutoComplete,
        SuggestionRow,
    },

    mixins: [needsOrganizationSlug],

    props: {
        // A whole row, not an id, so the pick renders without another request.
        modelValue: {
            required: false,
            type: Object,
            default: null,
        },
        // The server intersects these with what the caller may see.
        objectTypes: {
            required: true,
            type: Array,
        },
        view: {
            required: false,
            type: String,
            default: 'typeahead',
        },
        filters: {
            required: false,
            type: Array,
            default: () => [],
        },
        // URNs, not ids: ids collide across object types.
        excludeUrns: {
            required: false,
            type: Array,
            default: () => [],
        },
        // Maps a row's `meta` to a status from constants/suggestion-statuses.
        rowStatus: {
            required: false,
            type: Function,
            default: null,
        },
        // Emit each pick and stay a search box instead of showing the pick.
        clearOnSelect: {
            required: false,
            type: Boolean,
            default: false,
        },
        disabled: {
            required: false,
            type: Boolean,
            default: false,
        },
        placeholder: {
            required: false,
            type: String,
            default: 'Start typing to search',
        },
        emptySearchMessage: {
            required: false,
            type: String,
            default: 'Nothing matches that search',
        },
        inputId: {
            required: false,
            type: String,
            default: null,
        },
        // Non-empty fails native form validation on the input.
        validationMessage: {
            required: false,
            type: String,
            default: '',
        },
        dataTestIdPrefix: {
            required: true,
            type: String,
        },
    },

    emits: ['update:modelValue', 'select'],

    data() {
        return {
            // Typed text while searching; the pick lives in modelValue.
            draft: null,
            rows: [],
            total: 0,
            totalIsExact: true,
            page: 1,
            loading: false,
            loadingMore: false,
            loadError: null,
            hasTypedText: false,
            // Stops onFocus reopening a list the reader just closed.
            suppressFocusOpen: false,
            showValidationMessage: false,
            query: '',
            pendingRequest: null,
        }
    },

    computed: {
        validationMessageId() {
            return `${this.inputId ?? this.dataTestIdPrefix}-validation-message`
        },

        debounceMs: () => SEARCH_DEBOUNCE_MS,
        minLength: () => MIN_QUERY_LENGTH,
        optionTokens: () => OPTION_TOKENS,

        showPicked() {
            return Boolean(this.modelValue) && !this.clearOnSelect
        },

        shownCount() {
            return this.rows.length
        },

        // Rows are rehydrated from the DB and may drop deleted hits, so page
        // against the index total rather than rows.length.
        hasMore() {
            return this.page * PAGE_SIZE < this.total
        },

        nextPageSize() {
            return Math.min(PAGE_SIZE, this.total - this.rows.length)
        },

        // The list opens on focus, so the empty state also shows for an empty query.
        // Also announced to screen readers, so it says when a search is under way.
        activeEmptySearchMessage() {
            if (this.query.trim().length < MIN_QUERY_LENGTH) {
                return 'Start typing to search.'
            }

            return this.loading ? 'Searching…' : this.emptySearchMessage
        },

        requestShape() {
            return JSON.stringify([this.view, this.filters, this.objectTypes])
        },

        totalLabel() {
            const total = this.total.toLocaleString('en-US')

            return this.totalIsExact ? total : `${total}+`
        },

        // AutoComplete's optionDisabled takes a field name, not a callback.
        suggestions() {
            if (!this.rowStatus) {
                return this.rows
            }

            return this.rows.map((row) => {
                const status = this.rowStatus(row)

                return { ...row, status, unselectable: !isSelectable(status) }
            })
        },
    },

    watch: {
        requestShape() {
            if (this.query.trim().length >= MIN_QUERY_LENGTH) {
                this.page = 1
                this.rows = []
                this.search()
            }
        },

        validationMessage(message) {
            this.applyValidationMessage()

            if (!message) {
                this.showValidationMessage = false
            }
        },

        // Removing a pick renders a new input, which starts out valid.
        showPicked(picked) {
            if (!picked) {
                this.$nextTick(() => this.applyValidationMessage())
            }
        },
    },

    mounted() {
        this.applyValidationMessage()
    },

    beforeUnmount() {
        this.pendingRequest?.abort()
    },

    methods: {
        onInput(event) {
            const { value } = event.target
            this.hasTypedText = Boolean(value)

            // AutoComplete doesn't search or stay open when emptied, so stale
            // results would return on next focus.
            if (value.trim() === '') {
                this.query = ''
                this.resetResults()
                this.reopenWhileEmpty()
            }
        },

        // AutoComplete defers its own hide by a tick, so reopen after it.
        reopenWhileEmpty() {
            if (!this.$slots['overlay-header']) {
                return
            }

            setTimeout(() => {
                if (document.activeElement === this.inputElement()) {
                    this.$refs.autocomplete?.show()
                }
            }, 0)
        },

        // Open on focus so overlay-header controls are reachable without typing.
        onFocus() {
            this.showValidationMessage = false

            if (this.suppressFocusOpen) {
                this.suppressFocusOpen = false
                return
            }

            if (this.$slots['overlay-header']) {
                this.$nextTick(() => this.$refs.autocomplete?.show())
            }
        },

        // AutoComplete debounces this for us and withholds it below minLength.
        onComplete(event) {
            this.query = event.query
            this.page = 1
            // Empties the list so it shows "Searching…" rather than the last results.
            this.rows = []
            this.search()
        },

        onItemSelect(event) {
            this.$emit('select', event.value)

            if (this.clearOnSelect) {
                this.resetField()
                return
            }

            this.$emit('update:modelValue', event.value)
            this.draft = null
            this.hasTypedText = false
            this.resetResults()
        },

        async loadMore() {
            // mousedown.prevent means only the keyboard can focus the button.
            const fromKeyboard = document.activeElement === this.$refs.moreButton
            const firstNewRow = this.rows.length

            // The button disables while loading, which would drop focus to the page.
            if (fromKeyboard) {
                this.inputElement()?.focus()
            }

            this.page += 1
            await this.search(true)

            if (fromKeyboard && this.rows.length > firstNewRow) {
                await this.$nextTick()
                this.highlightFrom(firstNewRow)
            }
        },

        async search(isLoadMore = false) {
            // Without a prefix clause the index returns its head, not nothing.
            if ((this.query ?? '').trim().length < MIN_QUERY_LENGTH) {
                this.resetResults()
                return
            }

            // So a slow earlier search can't land late or report a stale error.
            this.pendingRequest?.abort()
            const controller = new AbortController()
            this.pendingRequest = controller

            this.loadError = null
            this[isLoadMore ? 'loadingMore' : 'loading'] = true

            try {
                const { data } = await this.$http.post(
                    `${this.organizationSlug}/search`,
                    {
                        view: this.view,
                        page: this.page,
                        count: PAGE_SIZE,
                        filters: this.buildFilters(),
                    },
                    { signal: controller.signal }
                )

                if (this.pendingRequest !== controller) {
                    return
                }

                this.rows = isLoadMore ? this.rows.concat(data.data) : data.data
                this.total = data.total ?? 0
                this.totalIsExact = data.total_is_exact !== false
            } catch (error) {
                if (this.isAbortError(error) || this.pendingRequest !== controller) {
                    return
                }

                // So the error isn't read as a caption on stale results.
                this.resetResults()
                this.loadError = 'Search is temporarily unavailable. Please try again.'
            } finally {
                if (this.pendingRequest === controller) {
                    this.loading = false
                    this.loadingMore = false
                    this.pendingRequest = null
                }
            }
        },

        buildFilters() {
            const filters = [
                // The view only picks the response shape; matching is a filter.
                {
                    field: TYPEAHEAD_FIELD,
                    operator: 'starts_with',
                    value: this.query.trim(),
                },
                {
                    field: 'object_type',
                    operator: 'in',
                    value: this.objectTypes,
                },
                ...this.filters,
            ]

            if (this.excludeUrns.length) {
                filters.push({
                    field: 'urn',
                    operator: 'not_in',
                    value: this.excludeUrns,
                })
            }

            return filters
        },

        /**
         * Capture phase, so this runs before AutoComplete sees the key.
         *
         * @param {KeyboardEvent} event
         */
        onFieldKeydown(event) {
            const input = this.inputElement()
            const { autocomplete } = this.$refs

            // AutoComplete picks the highlighted option on Enter but leaves the
            // default alone, so the form would submit too. Not stopped, so
            // AutoComplete still makes the pick.
            if (event.target === input && event.key === 'Enter') {
                event.preventDefault()
                return
            }

            // AutoComplete ignores ArrowDown while closed, stranding loaded results.
            if (
                event.target === input &&
                event.key === 'ArrowDown' &&
                !autocomplete?.overlayVisible &&
                this.rows.length
            ) {
                autocomplete?.show()
                return
            }

            if (
                event.target === input &&
                event.key === 'ArrowDown' &&
                this.isOnLastOption() &&
                this.moreButtonIsReady()
            ) {
                event.preventDefault()
                event.stopPropagation()
                this.$refs.moreButton.focus()
                return
            }

            if (event.key === 'Tab') {
                this.moveThroughStops(event)
            }
        },

        /**
         * The overlay is appended to the body, so default Tab would jump to the page end.
         *
         * @param {KeyboardEvent} event
         */
        onMoreKeydown(event) {
            if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
                event.preventDefault()
                this.highlightLastOption()
                return
            }

            if (event.key === 'Escape') {
                event.preventDefault()
                this.$refs.autocomplete?.hide()
                // Stop onFocus reopening the list.
                this.suppressFocusOpen = true
                this.inputElement()?.focus()
                return
            }

            if (event.key === 'Tab') {
                event.preventDefault()
                this.$refs.autocomplete?.hide()
                this.focusAfterField()
            }
        },

        isOnLastOption() {
            const { autocomplete } = this.$refs

            if (!autocomplete?.overlayVisible || autocomplete.focusedOptionIndex === -1) {
                return false
            }

            // Unselectable rows are skipped, so this may not be the last row.
            return autocomplete.focusedOptionIndex === autocomplete.findLastOptionIndex()
        },

        moreButtonIsReady() {
            return this.hasMore && !this.loadingMore && Boolean(this.$refs.moreButton)
        },

        // Focus stays in the input; AutoComplete highlights via aria-activedescendant.
        highlightLastOption() {
            const { autocomplete } = this.$refs

            this.inputElement()?.focus()
            autocomplete?.changeFocusedOptionIndex(null, autocomplete.findLastOptionIndex())
        },

        /**
         * Highlight the first selectable result at or after `index`, else the one before.
         *
         * @param {number} index
         */
        highlightFrom(index) {
            const { autocomplete } = this.$refs

            if (!autocomplete) {
                return
            }

            this.inputElement()?.focus()
            autocomplete.changeFocusedOptionIndex(null, autocomplete.findNextOptionIndex(index - 1))
        },

        /**
         * @param {KeyboardEvent} event
         */
        onHeaderKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault()
                this.$refs.autocomplete?.hide()
                // Stop onFocus reopening the list.
                this.suppressFocusOpen = true
                this.inputElement()?.focus()
                return
            }

            if (event.key === 'Tab') {
                this.moveThroughStops(event)
            }
        },

        /**
         * Tab stops that currently exist. 'options' is the input with a result
         * highlighted, since results never take real focus.
         *
         * @returns {string[]}
         */
        tabStops() {
            const { autocomplete } = this.$refs
            const open = Boolean(autocomplete?.overlayVisible)
            const stops = ['field']

            if (this.clearControl()) {
                stops.push('clear')
            }

            if (open && this.firstInHeader()) {
                stops.push('header')
            }

            if (open && autocomplete.findFirstOptionIndex() !== -1) {
                stops.push('options')
            }

            return stops
        },

        /**
         * @param {EventTarget} target
         * @returns {string|null}
         */
        stopOf(target) {
            const { autocomplete } = this.$refs

            if (target === this.inputElement()) {
                return autocomplete?.overlayVisible && autocomplete.focusedOptionIndex !== -1 ? 'options' : 'field'
            }

            if (target === this.clearControl()) {
                return 'clear'
            }

            if (this.$refs.overlayHeader?.contains(target)) {
                return 'header'
            }

            return null
        },

        /**
         * Default Tab would let AutoComplete select the highlighted row, and
         * from the body-appended header would jump to the end of the page.
         *
         * @param {KeyboardEvent} event
         */
        moveThroughStops(event) {
            const here = this.stopOf(event.target)

            if (!here) {
                return
            }

            const stops = this.tabStops()
            const next = stops[stops.indexOf(here) + (event.shiftKey ? -1 : 1)]

            if (next) {
                event.preventDefault()
                event.stopPropagation()
                this.goToStop(next)
                return
            }

            // Browser default is fine for Shift+Tab out of the field.
            if (event.shiftKey) {
                return
            }

            // Browser order is fine here. AutoComplete closes the list itself
            // from the input but never sees keys on the clear control.
            if (here === 'field' || here === 'clear') {
                if (here === 'clear') {
                    this.$refs.autocomplete?.hide()
                }
                return
            }

            // From the header or results: continue after the field, not the
            // overlay, without AutoComplete selecting the highlighted row.
            event.preventDefault()
            event.stopPropagation()
            this.$refs.autocomplete?.hide()
            this.focusAfterField()
        },

        /**
         * @param {string} stop
         */
        goToStop(stop) {
            const { autocomplete } = this.$refs
            const input = this.inputElement()

            if (stop === 'field') {
                if (autocomplete) {
                    autocomplete.focusedOptionIndex = -1
                }
                input?.focus()
            } else if (stop === 'clear') {
                this.clearControl()?.focus()
            } else if (stop === 'header') {
                this.firstInHeader()?.focus()
            } else if (stop === 'options') {
                input?.focus()
                autocomplete?.changeFocusedOptionIndex(null, autocomplete.findFirstOptionIndex())
            }
        },

        clearControl() {
            return this.$el?.querySelector?.('.typeahead-field__clear') ?? null
        },

        firstInHeader() {
            return (this.$refs.overlayHeader && focusables(this.$refs.overlayHeader)[0]) ?? null
        },

        // Skips the overlay, which sits at the end of the body.
        focusAfterField() {
            const overlay = this.$refs.overlayHeader?.closest('.p-autocomplete-overlay')
            const own = this.clearControl() ?? this.inputElement()
            const inPage = focusables(document).filter((element) => !overlay?.contains(element))
            const next = inPage[inPage.indexOf(own) + 1]

            if (next) {
                next.focus()
            }
        },

        // A click here lands outside AutoComplete, which closes the list a
        // tick later, so reopen after that.
        clearText() {
            this.resetField({ close: false })
            this.$nextTick(() => {
                this.inputElement()?.focus()
                this.reopenWhileEmpty()
            })
        },

        removeSelection() {
            this.$emit('update:modelValue', null)
            this.resetField()
            this.$nextTick(() => this.inputElement()?.focus())
        },

        resetField({ close = true } = {}) {
            this.draft = null
            this.hasTypedText = false
            this.query = ''
            this.resetResults()

            this.$nextTick(() => {
                const input = this.inputElement()

                // Typed text never reached the model, so clear the input directly.
                if (input) {
                    input.value = ''
                }

                if (close) {
                    this.$refs.autocomplete?.hide()
                }
            })
        },

        resetResults() {
            this.pendingRequest?.abort()
            this.pendingRequest = null
            this.rows = []
            this.total = 0
            this.totalIsExact = true
            this.page = 1
            this.loading = false
            this.loadingMore = false
        },

        inputElement() {
            return this.$refs.autocomplete?.$el?.querySelector('input')
        },

        applyValidationMessage() {
            this.inputElement()?.setCustomValidity(this.validationMessage)
        },

        // Suppress the native bubble, which would open the list and cover it.
        onInvalid(event) {
            event.preventDefault()
            this.showValidationMessage = true
        },

        isAbortError(error) {
            return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError'
        },
    },
}
</script>

<style lang="scss" scoped>
// The body sets `color: #fff`, so every light background here sets its own
// text colour to avoid white on white.
.typeahead-field {
    &__search {
        position: relative;
    }

    // Match the app's other inputs.
    :deep(.p-autocomplete) {
        width: 100%;
    }

    :deep(.p-autocomplete-input) {
        width: 100%;
        height: 36px;
        padding: 0 36px 0 12px;
        border: 0;
        border-radius: 5px;
        background: #fff;
        color: var(--p-text-color, #212628);
        box-shadow: none;

        &:enabled:hover,
        &:enabled:focus {
            border: 0;
            box-shadow: none;
            outline: 0;
        }
    }

    &__picked {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 36px;
        padding: 6px 8px 6px 11px;
        border: 0;
        border-radius: 5px;
        background: #fff;
        color: var(--p-text-color, #212628);
    }

    &__remove {
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        border: 0;
        cursor: pointer;
        line-height: 1;
        padding: 0;
    }

    &__remove {
        margin-left: auto;
        width: 26px;
        height: 26px;
        border-radius: 5px;
        background: transparent;
        color: var(--p-text-muted-color, #6b7280);
        font-size: 1.15rem;

        &:hover {
            color: #b3261e;
        }
    }

    // Same control the assets keyword search uses.
    &__clear {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        color: var(--p-text-muted-color, #6b7280);
        font-size: 0.85rem;
        cursor: pointer;

        &:hover {
            color: var(--p-text-color, #212628);
        }
    }

    &__overlay-header {
        padding: 8px 12px;
        border-bottom: 1px solid var(--p-content-border-color, #e5e7eb);
    }

    &__footer {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 7px 12px;
        border-top: 1px solid var(--p-content-border-color, #e5e7eb);
        font-size: 0.8rem;
        color: var(--p-text-muted-color, #6b7280);
    }

    &__more {
        margin-left: auto;
        border: 1px solid var(--p-content-border-color, #e5e7eb);
        border-radius: 5px;
        background: var(--p-surface-50, #f6f8f9);
        color: var(--p-text-color, #212628);
        font: inherit;
        font-size: 0.8rem;
        padding: 3px 10px;
        cursor: pointer;

        &:hover:enabled {
            background: var(--p-surface-200, #e5e7eb);
        }

        // Reachable by arrow keys, so focus must be as visible as a highlighted row.
        &:focus-visible {
            outline: 2px solid var(--p-primary-color, #10b981);
            outline-offset: 2px;
        }

        &:disabled {
            cursor: default;
            opacity: 0.6;
        }

        .pi-spinner {
            margin-right: 4px;
            font-size: 0.75rem;
        }
    }

    &__loading {
        display: inline-flex;
        align-items: center;
        gap: 7px;

        .pi-spinner {
            font-size: 0.875rem;
        }
    }

    // Sits on the dark page, so uses the app's inline form-error red.
    &__error {
        margin: 7px 0 0;
        font-size: 0.8rem;
        color: #f96a62;
    }
}
</style>
